import type { Texture } from '@seddi/three';
import pica, { type Pica, type PicaResizeOptions } from 'pica';

import { assert } from '../core/assert';

const picaOnce = (() => {
  let picaInstance: Pica | null = null;
  return () => {
    if (picaInstance === null) {
      picaInstance = pica();
    }
    return picaInstance;
  };
})();

/**
 * Get pixels in CPU.
 * @param texture Texture.
 */
const getTextureData = (texture: Texture): ImageData => {
  // This is the only way, right now, to get the data in the CPU for an imported image
  // https://stackoverflow.com/questions/68102995/is-it-possible-to-get-pixels-from-imagebitmap-web-object-without-a-canvas
  // The recommended way is to render it on a canvas
  // https://developer.mozilla.org/en-US/docs/Web/API/ImageBitmap

  const canvas = document.createElement('canvas');
  canvas.height = texture.image.height;
  canvas.width = texture.image.width;

  const context = canvas.getContext('2d');
  context.scale(1, -1);
  context.drawImage(texture.image, 0, -canvas.height, canvas.width, canvas.height);

  const data = context.getImageData(0, 0, canvas.width, canvas.height);
  return data;
};

const toImageDataArray = (elements: Iterable<number>): ImageDataArray => {
  return Uint8ClampedArray.from(elements);
};

/**
 * Applies a 3×3 Gaussian blur via two 1D passes (horizontal then vertical),
 * using wrap-around sampling at the borders so tiles stay continuous.
 *
 * Kernel 1D: [1, 2, 1] normalized by 4.
 */
const gaussianBlur = (
  data: ImageDataArray,
  width: number,
  height: number,
  channels: number,
): ImageDataArray => {
  const len = data.length;
  const temp = new Uint8ClampedArray(len);
  const out = new Uint8ClampedArray(len);
  const stride = width * channels;

  // 1) Horizontal pass with wrap-around
  for (let y = 0; y < height; ++y) {
    const rowOffset = y * stride;
    for (let x = 0; x < width; ++x) {
      const xm1 = (x - 1 + width) % width;
      const xp1 = (x + 1) % width;
      const base = rowOffset + x * channels;

      for (let c = 0; c < channels; ++c) {
        const left = data[rowOffset + xm1 * channels + c];
        const mid = data[base + c];
        const right = data[rowOffset + xp1 * channels + c];
        // (1 * left + 2 * mid + 1 * right) / 4
        temp[base + c] = (left * 1 + mid * 2 + right * 1) / 4;
      }
    }
  }

  // 2) Vertical pass with wrap-around
  for (let y = 0; y < height; ++y) {
    const ym1 = (y - 1 + height) % height;
    const yp1 = (y + 1) % height;
    const rowOff = y * stride;
    const rowOffM = ym1 * stride;
    const rowOffP = yp1 * stride;

    for (let x = 0; x < width; ++x) {
      const base = rowOff + x * channels;
      for (let c = 0; c < channels; ++c) {
        const up = temp[rowOffM + x * channels + c];
        const mid = temp[base + c];
        const down = temp[rowOffP + x * channels + c];
        // (1 * up + 2 * mid + 1 * down) / 4
        out[base + c] = (up * 1 + mid * 2 + down * 1) / 4;
      }
    }
  }

  return out;
};

/**
 * Generate high-quality mipmaps.
 * @param texture Renderer texture.
 * @param tags Texture tags.
 */
export const generateTextureMips = async (texture: Texture, tags?: string[]) => {
  let imageData!: ImageData;
  {
    const isDataImg = texture.image.data != undefined;
    let data: ImageDataArray = !isDataImg
      ? getTextureData(texture).data
      : toImageDataArray(texture.image.data);
    let height = texture.image.height;
    let width = texture.image.width;

    imageData = new ImageData(data, width, height);
  }

  // enable mipmap filtering only for fabric materials.
  // const withGaussianBlur = options.mipmapsUnsharp;

  // Legacy
  // {
  //   texture.mipmaps = generateLegacyMips(imageData, withGaussianBlur);
  // }
  // Hamming
  // {
  //   texture.mipmaps = await generatePicaMips(imageData, { filter: 'hamming' });
  // }
  // Lanczos, win = 2
  // {
  //   texture.mipmaps = await generatePicaMips(imageData, { filter: 'lanczos2' });
  // }
  // Magic Kernel Sharp 2013, win = 2.5
  {
    texture.mipmaps = await generatePicaMips(imageData, { filter: 'mks2013' });
  }

  texture.generateMipmaps = false;
};

const generateLegacyMips = (image: ImageData, unsharp = false): ImageData[] => {
  const toIndex = (x: number, y: number, width: number) => y * width + x;

  const channels = image.data.length / (image.width * image.height);
  assert(channels === 3 || channels === 4, 'Unsupported format generate legacy mips');

  const mips: ImageData[] = [image];
  let currentImageData = image;

  // Box filter for both dimensions
  while (currentImageData.width > 1 && currentImageData.height > 1) {
    const { height, width } = currentImageData;
    let { data } = currentImageData;

    // Apply Gaussian blur convolution
    if (unsharp) {
      data = gaussianBlur(data, width, height, channels);
    }

    const newWidth = Math.max(1, Math.floor(width / 2));
    const newHeight = Math.max(1, Math.floor(height / 2));
    const newData = new Uint8ClampedArray(newWidth * newHeight * channels);

    for (let y = 0; y < newHeight; ++y) {
      for (let x = 0; x < newWidth; ++x) {
        // average 4 samples
        const osx = x * 2;
        const osy = y * 2;
        const o1 = toIndex(osx, osy, currentImageData.width) * channels;
        const o2 = o1 + channels;
        const o3 = toIndex(osx, osy + 1, currentImageData.width) * channels;
        const o4 = o3 + channels;
        const destIndex = toIndex(x, y, newWidth) * channels;
        newData[destIndex] = (data[o1] + data[o2] + data[o3] + data[o4]) * 0.25;
        newData[destIndex + 1] = (data[o1 + 1] + data[o2 + 1] + data[o3 + 1] + data[o4 + 1]) * 0.25;
        newData[destIndex + 2] = (data[o1 + 2] + data[o2 + 2] + data[o3 + 2] + data[o4 + 2]) * 0.25;
        if (channels === 4) {
          newData[destIndex + 3] = (data[o1 + 3] + data[o2 + 3] + data[o3 + 3] + data[o4 + 3]) * 0.25;
        }
      }
    }

    const newImageData = new ImageData(newData, newWidth, newHeight);
    mips.push(newImageData);
    currentImageData = newImageData;
  }

  // handle the “collapsed” case when one dimension reaches 1 first
  while (currentImageData.width > 1 || currentImageData.height > 1) {
    const { height, width } = currentImageData;
    let { data } = currentImageData;

    // Apply Gaussian blur convolution
    if (unsharp) {
      data = gaussianBlur(data, width, height, channels);
    }

    const newWidth = Math.max(1, Math.floor(width / 2));
    const newHeight = Math.max(1, Math.floor(height / 2));
    const newData = new Uint8ClampedArray(channels * newWidth * newHeight);

    for (let y = 0; y < newHeight; y++) {
      for (let x = 0; x < newWidth; x++) {
        // average 2 samples
        let o1!: number;
        let o2!: number;

        if (width === 1) {
          // vertical collapse
          const osy = y * 2;
          o1 = toIndex(0, osy, width) * channels;
          o2 = toIndex(0, osy + 1, width) * channels;
        } else {
          // horizontal collapse
          const osx = x * 2;
          o1 = toIndex(osx, 0, width) * channels;
          o2 = toIndex(osx + 1, 0, width) * channels;
        }

        const destIndex = toIndex(x, y, newWidth) * channels;
        newData[destIndex] = (data[o1] + data[o2]) * 0.5;
        newData[destIndex + 1] = (data[o1 + 1] + data[o2 + 1]) * 0.5;
        newData[destIndex + 2] = (data[o1 + 2] + data[o2 + 2]) * 0.5;
        if (channels === 4) {
          newData[destIndex + 3] = (data[o1 + 3] + data[o2 + 3]) * 0.5;
        }
      }
    }

    const newImageData = new ImageData(newData, newWidth, newHeight);
    mips.push(newImageData);
    currentImageData = newImageData;
  }

  return mips;
};

const generatePicaMips = async (image: ImageData, config: PicaResizeOptions = {}): Promise<ImageData[]> => {
  const picaInstance = picaOnce();
  const mips: ImageData[] = [image];
  let currentImageData = image;

  while (currentImageData.width > 1 || currentImageData.height > 1) {
    const fromCanvas = document.createElement('canvas');
    fromCanvas.width = currentImageData.width;
    fromCanvas.height = currentImageData.height;
    const fromCtx = fromCanvas.getContext('2d');
    if (!fromCtx) throw new Error('Could not get 2d context from canvas');
    fromCtx.putImageData(currentImageData, 0, 0);

    const newWidth = Math.max(1, Math.floor(currentImageData.width / 2));
    const newHeight = Math.max(1, Math.floor(currentImageData.height / 2));

    const toCanvas = document.createElement('canvas');
    toCanvas.width = newWidth;
    toCanvas.height = newHeight;

    await picaInstance.resize(fromCanvas, toCanvas, config);

    const toCtx = toCanvas.getContext('2d');
    if (!toCtx) throw new Error('Could not get 2d context from canvas');

    const newImageData = toCtx.getImageData(0, 0, newWidth, newHeight);
    mips.push(newImageData);
    currentImageData = newImageData;
  }

  return mips;
};
